/**
 * v0.5 経営会議PDCA 増分「ブリーフ対話AI」の対話パネル。
 * 設計: docs/design/v05-board-meeting-chat-detail.md §7 (P-1〜P-17)。
 * 配置は BoardMeetingDetailPage.tsx の <section>(ブリーフ) の直後に独立した <section> として置く
 * (P-9: ブリーフの多分岐三項の内部に置くと生成中/失敗時/臨時会議で対話が描画されない、§7参照)。
 *
 * ⚠️ 共有スレッド (複数参加者が同じ会議に同時アクセスしうる) のため、SSE (送信者のみに届く) に加え
 * GET を差分ポーリングして他参加者の発言を拾う (§4-1 ポーリング契約)。
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, Lightbulb, Loader2, Send, Sparkles, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { postSSE } from '@/lib/sse';
import { isNearBottom } from '@/lib/chat-scroll';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/Modal';
import { CreateInitiativeFromChatModal } from './CreateInitiativeFromChatModal';

type ChatFinishReason = 'complete' | 'max_iterations' | 'timeout' | 'quota' | 'error' | 'aborted';
type ChatEvidence = { store_ids: string[]; months?: string[]; measure?: string; excluded_count?: number };
type ChatMessage = {
  seq: number;
  role: 'user' | 'assistant';
  display_text: string | null;
  evidence: ChatEvidence | null;
  finish_reason: ChatFinishReason | null;
  author_display_name: string | null;
  created_at: string;
  ai_reusable: boolean;
};
type ChatSession = { status: 'idle' | 'generating'; generation_started_at: string | null; owner_display_name: string | null };
type ChatGetResponse = {
  session: ChatSession | null;
  messages: ChatMessage[];
  can_aggregate: boolean;
  can_aggregate_reason: string | null;
};

const MEASURE_LABEL: Record<string, string> = {
  sales_amount: '売上金額', quantity: '数量', cost_amount: '原価金額',
  opex_amount: '諸経費金額', labor_cost_amount: '人件費金額',
};

// P-16: finish_reason 6値のうち、途中終了 (打ち切り) を示す5値の案内文。complete は何も表示しない。
const FINISH_REASON_BANNER: Partial<Record<ChatFinishReason, string>> = {
  max_iterations: '集計の上限に達したため途中で終了しました。数値は不完全な場合があります。追加で質問してみてください。',
  timeout: '応答に時間がかかりすぎたため途中で終了しました。数値が不完全な場合があります。追加で質問してみてください。',
  quota: 'この時間のAI利用上限に達したため途中で終了しました。しばらくしてから再度お試しください。',
  error: '処理中にエラーが発生したため、この回答は途中で終了しました。もう一度質問してみてください。',
  aborted: '生成が中断されたため、この回答は途中で終了しました。もう一度質問してみてください。',
};

const CAN_AGGREGATE_REASON_LABEL: Record<string, string> = {
  no_dashboard_scope: 'ダッシュボードの閲覧権限が無いため、この会議のデータをAIに集計させることはできません。',
};

const DEFAULT_QUESTION_CHIPS = ['先月と比べて何が変わった?', '原価率が上がった要因は?', '客単価の推移は?'];

// P-9b (design-expert CRITICAL C-2 是正、2026-09-16): サーバーの占有stale閾値 (apps/api の
// CHAT_CLAIM_STALE_MS) と同じ3分。バックエンドはこの時間を過ぎたgeneratingを再claim可能だが、
// フロントが raw な status だけを見て composer を無条件disabledにすると、送信者が離脱した場合に
// 「再送するボタンが押せない」構造的なロック状態になる (誰も送信できないため再claimの契機が
// 発生しない)。stale化を検知したら composer を再度有効化し、文言も専用のものに切り替える。
const CHAT_CLAIM_STALE_MS = 3 * 60 * 1000;

const formatElapsed = (sec: number) => (sec < 60 ? `${sec}秒` : `${Math.floor(sec / 60)}分${sec % 60}秒`);
const formatTime = (iso: string) => new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });

export function BoardChatPanel({
  meetingId, topFindings, canManage,
}: {
  meetingId: string;
  /** P-11 空状態の質問チップ生成元 (論点の上位3件、呼出側でソート済みのものを渡すこと) */
  topFindings: Array<{ finding_id: string; title: string }>;
  /** P-5b 全消去ボタンの出し分け。board_meeting:manage は all_stores 限定のため
   *  hasTenantWidePermission で判定した値をそのまま渡す (呼出側 = BoardMeetingDetailPage の既存 canManage と同型)。 */
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  // ⚠️ P-5: 施策化ボタンの出し分けは hasTenantWidePermission を使わない (tenant-wide 保有者以外が
  // 常に非表示になるバグを避けるため、design §7参照)。node 側の書込み可否はモーダル内の
  // creatable-nodes 一覧が担保する。
  const canCreateInitiative = hasPermissionForFeatureAction('board_initiative', 'create');
  const myDisplayName = useAuthStore((s) => s.user?.display_name ?? s.user?.name ?? '自分');

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [canAggregate, setCanAggregate] = useState<boolean | null>(null);
  const [canAggregateReason, setCanAggregateReason] = useState<string | null>(null);
  const maxSeqRef = useRef(0);

  const [input, setInput] = useState('');
  const [sendingViaSSE, setSendingViaSSE] = useState(false);
  const [optimisticUserText, setOptimisticUserText] = useState<string | null>(null);
  const [streamingAssistantText, setStreamingAssistantText] = useState('');
  const [showNewMessagePill, setShowNewMessagePill] = useState(false);
  // design-expert HIGH-2 是正 (2026-09-16): P-13「新着の未読件数も出す」。真偽値だけでなく件数を保持する。
  const [unreadCount, setUnreadCount] = useState(0);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [initiativeSource, setInitiativeSource] = useState<{ title: string; description: string } | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const containerRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const prevRenderCountRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // 会議が切り替わったら (通常は起こらないが、念のため) 累積状態をリセットする
  useEffect(() => {
    setMessages([]); maxSeqRef.current = 0; setSession(null);
    setCanAggregate(null); setCanAggregateReason(null);
    stickToBottomRef.current = true; prevRenderCountRef.current = 0;
  }, [meetingId]);

  // §4-1 ポーリング契約: status==='generating' の間は2000ms、平常は15000ms。差分は after_seq で取得する。
  const chatQuery = useQuery<ChatGetResponse>({
    queryKey: ['board-meeting-chat', meetingId],
    queryFn: () => apiFetch<ChatGetResponse>(`/board-meetings/${meetingId}/chat?after_seq=${maxSeqRef.current}`),
    refetchInterval: (query) => (query.state.data?.session?.status === 'generating' ? 2000 : 15000),
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    const data = chatQuery.data;
    if (!data) return;
    setSession(data.session);
    setCanAggregate(data.can_aggregate);
    setCanAggregateReason(data.can_aggregate_reason);
    if (data.messages.length > 0) {
      setMessages((prev) => [...prev, ...data.messages]);
      maxSeqRef.current = data.messages.reduce((m, row) => Math.max(m, row.seq), maxSeqRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatQuery.data]);

  // 生成中の経過秒数表示 (P-4/P-7b) 用の1秒ティック
  useEffect(() => {
    if (session?.status !== 'generating') return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [session?.status]);
  const generatingElapsedSec = session?.status === 'generating' && session.generation_started_at
    ? Math.max(0, Math.floor((nowTick - new Date(session.generation_started_at).getTime()) / 1000))
    : null;
  // P-9b: stale化 (3分超) した占有は、他の参加者からの送信で奪える (サーバー側の§4-2 stale回復)。
  const isStaleGenerating = session?.status === 'generating' && session.generation_started_at
    ? nowTick - new Date(session.generation_started_at).getTime() > CHAT_CLAIM_STALE_MS
    : false;

  // ⚠️ サーバーは tool 呼出中の「中間ターン」も role='assistant' として1行ずつ永続化する
  // (board-meetings.ts の onEvent 'assistant' が反復ごとに INSERT するため)。中間ターンは
  // 表示テキストを持たないことが多く (ツール呼出のみで finish_reason も null)、そのまま描画すると
  // 空のAI吹き出しが並ぶ。display_text か finish_reason のどちらかを持つ行だけを表示対象にする。
  const visibleMessages = messages.filter((m) => m.role === 'user' || m.display_text || m.finish_reason);

  const composerDisabled = sendingViaSSE || (session?.status === 'generating' && !isStaleGenerating);

  // P-2: 自動スクロール。他者発言で上方閲覧者を引っ張らない (isNearBottom, threshold=60既定)。
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = isNearBottom(el.scrollHeight, el.scrollTop, el.clientHeight);
      stickToBottomRef.current = atBottom;
      if (atBottom) { setShowNewMessagePill(false); setUnreadCount(0); }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    } else if (visibleMessages.length > prevRenderCountRef.current) {
      setShowNewMessagePill(true);
      setUnreadCount((n) => n + (visibleMessages.length - prevRenderCountRef.current));
    }
    prevRenderCountRef.current = visibleMessages.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleMessages.length, streamingAssistantText, optimisticUserText]);

  const scrollToBottom = () => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickToBottomRef.current = true;
    setShowNewMessagePill(false);
    setUnreadCount(0);
  };

  const send = async () => {
    const content = input.trim();
    if (!content || composerDisabled) return;
    setInput('');
    setSendingViaSSE(true);
    setOptimisticUserText(content);
    setStreamingAssistantText('');
    // 楽観的にロック表示へ切り替える (実際の反映は直後の refetch で確定する、finally 参照)。
    setSession({ status: 'generating', generation_started_at: new Date().toISOString(), owner_display_name: myDisplayName });
    stickToBottomRef.current = true;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      await postSSE(`/board-meetings/${meetingId}/chat/messages`, { content }, {
        onEvent: (event, data) => {
          if (event === 'delta') {
            setStreamingAssistantText((prev) => prev + (JSON.parse(data) as { text: string }).text);
          } else if (event === 'error') {
            toast.error((JSON.parse(data) as { message: string }).message);
          }
        },
      }, controller.signal);
    } catch (e) {
      if (!controller.signal.aborted) {
        toast.error(e instanceof Error ? e.message : 'メッセージの送信に失敗しました');
      }
    } finally {
      await chatQuery.refetch();
      setOptimisticUserText(null);
      setStreamingAssistantText('');
      setSendingViaSSE(false);
      abortControllerRef.current = null;
      inputRef.current?.focus();
    }
  };

  // P-7b: 「停止」ではない。SSE の購読 (fetch) だけ解除し、以降はポーリングに委ねる。
  // サーバー側の生成は続くため、生成中ロックは解除されない (画面からは消えない)。
  const returnToInput = () => { abortControllerRef.current?.abort(); };

  const deleteAllMut = useMutation({
    mutationFn: () => apiFetch<{ deleted_count: number }>(`/board-meetings/${meetingId}/chat`, { method: 'DELETE' }),
    onSuccess: () => {
      setMessages([]); maxSeqRef.current = 0; setSession(null);
      setDeleteAllConfirm(false);
      toast.success('対話ログを削除しました');
      qc.invalidateQueries({ queryKey: ['board-meeting-chat', meetingId] });
    },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteAllConfirm(false); },
  });

  const questionChips = topFindings.length > 0
    ? topFindings.slice(0, 3).map((f) => `${f.title}について詳しく教えてください`)
    : DEFAULT_QUESTION_CHIPS;

  const showEmptyState = visibleMessages.length === 0 && !optimisticUserText && !streamingAssistantText;

  return (
    <section className="border border-surface-border rounded-lg bg-white flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-surface-line flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-semibold text-text-body flex items-center gap-1.5">
          <Sparkles size={15} className="text-brand-red" aria-hidden /> AIに質問する
        </h2>
        {canManage && visibleMessages.length > 0 && (
          <Button variant="ghost" size="sm" leftIcon={<Trash2 size={13} />}
            disabled={session?.status === 'generating'}
            title={session?.status === 'generating' ? '生成中は削除できません' : undefined}
            onClick={() => setDeleteAllConfirm(true)}>
            ログを全て削除
          </Button>
        )}
      </div>

      <div className="relative">
        <div
          ref={containerRef}
          role="log"
          aria-live="polite"
          className="max-h-[60vh] overflow-y-auto px-4 py-3 space-y-3"
        >
          {chatQuery.isLoading && messages.length === 0 && (
            <p className="text-sm text-text-sub">読み込んでいます…</p>
          )}
          {chatQuery.isError && visibleMessages.length === 0 && (
            <p role="alert" className="text-sm text-alert-text">対話ログの取得に失敗しました。しばらくすると自動的に再試行します。</p>
          )}
          {showEmptyState && !chatQuery.isLoading && (
            <div className="space-y-2 py-4">
              <p className="text-sm text-text-sub text-center">
                この会議のデータについて、AIに質問できます (店舗横断の集計や傾向など)。
              </p>
              <div className="flex flex-wrap gap-2 justify-center" role="group" aria-label="質問の例">
                {questionChips.map((q) => (
                  <button key={q} type="button" onClick={() => setInput(q)}
                    className="inline-flex items-center px-3 py-1.5 rounded-full border border-surface-border text-xs text-text-sub hover:bg-surface-sunken min-h-[44px]">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {visibleMessages.map((m) => (
            <div key={m.seq} className="flex flex-col items-start gap-0.5">
              <div className="flex items-center gap-1.5 text-[11px] text-text-sub">
                {m.role === 'assistant' ? (
                  <>
                    {/* design-expert CRITICAL C-1 是正 (2026-09-16): brand-redはAIメッセージ件数分
                        繰り返されると「1画面1箇所」の原則を壊し送信ボタン(CTA)との識別性も失う
                        (CLAUDE.md ui-guidelines §2.2)。反復するバッジはニュートラル色にする。 */}
                    <Sparkles size={12} className="text-text-sub" aria-hidden />
                    <span className="font-semibold">AI</span>
                  </>
                ) : (
                  <span className="font-semibold">{m.author_display_name ?? '参加者'}</span>
                )}
                <span className="tabular-nums">{formatTime(m.created_at)}</span>
              </div>
              {m.display_text ? (
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                  m.role === 'assistant' ? 'bg-surface-sunken text-text-main' : 'bg-white border border-surface-border text-text-main'
                }`}>
                  {m.display_text}
                </div>
              ) : null}
              {m.role === 'assistant' && m.finish_reason && m.finish_reason !== 'complete' && (
                <div className="max-w-[80%] rounded-lg bg-alert-bg text-alert-text text-xs p-2 flex items-start gap-1.5">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" aria-hidden />
                  <span className="break-words">{FINISH_REASON_BANNER[m.finish_reason]}</span>
                </div>
              )}
              {m.role === 'assistant' && !m.ai_reusable && (
                <span className="inline-block max-w-[80%] text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border">
                  この回答はあなたの権限範囲外の店舗を含むため、追加の質問には使えません
                </span>
              )}
              {m.role === 'assistant' && m.display_text && (
                <details className="max-w-[80%]">
                  <summary className="text-[11px] text-text-sub cursor-pointer">根拠</summary>
                  <ul className="text-[11px] text-text-sub mt-1 space-y-0.5 pl-3 list-disc">
                    <li>店舗: {m.evidence && m.evidence.store_ids.length > 0 ? m.evidence.store_ids.join('、') : '—'}</li>
                    <li>対象月: {m.evidence?.months && m.evidence.months.length > 0 ? m.evidence.months.join('、') : '—'}</li>
                    <li>指標: {m.evidence?.measure ? (MEASURE_LABEL[m.evidence.measure] ?? m.evidence.measure) : '—'}</li>
                    <li>除外: {m.evidence?.excluded_count ? `未公開 ${m.evidence.excluded_count}件を除外` : '除外なし'}</li>
                    <li>AI再利用: {m.ai_reusable ? '可' : '不可'}</li>
                  </ul>
                </details>
              )}
              {m.role === 'assistant' && m.display_text && canCreateInitiative && (
                <Button variant="ghost" size="sm" leftIcon={<Lightbulb size={13} />}
                  onClick={() => setInitiativeSource({
                    title: m.display_text!.split('\n')[0]!.slice(0, 60),
                    description: m.display_text!,
                  })}>
                  施策化
                </Button>
              )}
            </div>
          ))}

          {optimisticUserText && (
            <div className="flex flex-col items-start gap-0.5">
              <div className="flex items-center gap-1.5 text-[11px] text-text-sub">
                <span className="font-semibold">{myDisplayName}</span>
              </div>
              <div className="max-w-[80%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words bg-white border border-surface-border text-text-main">
                {optimisticUserText}
              </div>
            </div>
          )}
          {sendingViaSSE && (
            <div className="flex flex-col items-start gap-0.5">
              <div className="flex items-center gap-1.5 text-[11px] text-text-sub">
                <Sparkles size={12} className="text-text-sub" aria-hidden />
                <span className="font-semibold">AI</span>
              </div>
              {streamingAssistantText ? (
                <div className="max-w-[80%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words bg-surface-sunken text-text-main" aria-live="polite">
                  {streamingAssistantText}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-text-sub">
                  <Loader2 size={14} className="animate-spin" aria-hidden />
                  生成中です{generatingElapsedSec != null && <span className="tabular-nums"> (経過{formatElapsed(generatingElapsedSec)})</span>}
                </div>
              )}
            </div>
          )}
        </div>

        {showNewMessagePill && (
          // design-expert CRITICAL C-1 (brand-redの反復使用回避) / HIGH-2 (未読件数表示) 是正
          <button type="button" onClick={scrollToBottom}
            className="absolute bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 rounded-full bg-text-main text-white text-xs min-h-[44px]">
            <ChevronDown size={13} aria-hidden /> 新しい発言{unreadCount > 0 ? ` (${unreadCount}件)` : ''}
          </button>
        )}
      </div>

      {session?.status === 'generating' && (
        <div className="px-4 py-2 border-t border-surface-line bg-surface-sunken text-xs text-text-sub flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-1.5">
            {/* P-9b: stale化(3分超)した占有は「送信できない」ではなく「再送できる」ことを伝える
                (composerDisabledは既にfalseへ切り替わっているため、入力欄自体は使える)。 */}
            {!sendingViaSSE && isStaleGenerating ? (
              <>
                <AlertTriangle size={13} aria-hidden />
                他の参加者の質問が応答しませんでした。再送できます
              </>
            ) : (
              <>
                <Loader2 size={13} className="animate-spin" aria-hidden />
                {sendingViaSSE
                  ? '生成は続き、完了後に全員へ表示されます'
                  : `${session.owner_display_name ?? '参加者'}さんが質問中です`}
                {generatingElapsedSec != null && <span className="tabular-nums"> (経過{formatElapsed(generatingElapsedSec)})</span>}
                {!sendingViaSSE && visibleMessages.at(-1)?.role === 'user' && (
                  <span className="break-words">：「{visibleMessages.at(-1)?.display_text}」</span>
                )}
              </>
            )}
          </span>
          {sendingViaSSE && (
            <Button variant="ghost" size="sm" onClick={returnToInput}>入力に戻る</Button>
          )}
        </div>
      )}

      {canAggregate === false ? (
        <div className="p-3 border-t border-surface-line text-xs text-text-sub bg-surface-sunken">
          {CAN_AGGREGATE_REASON_LABEL[canAggregateReason ?? ''] ?? 'この会議のデータを集計する権限がないため、対話機能は利用できません。'}
          {' '}これまでの対話ログの閲覧は引き続き可能です。
        </div>
      ) : canAggregate === true ? (
        <div className="p-3 border-t border-surface-line">
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="この会議のデータについて質問できます (Shift+Enterで送信)"
              rows={2}
              maxLength={2000}
              disabled={composerDisabled}
              aria-label="AIへの質問"
              className="flex-1 border border-surface-border rounded px-3 py-2 text-sm resize-none disabled:opacity-50 disabled:bg-surface-sunken"
            />
            <Button variant="primary" size="md" disabled={composerDisabled || !input.trim()}
              onClick={send} aria-label="送信" leftIcon={<Send size={16} />}>
              送信
            </Button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={deleteAllConfirm}
        title="対話ログを削除しますか"
        destructive
        confirmLabel="削除する"
        loading={deleteAllMut.isPending}
        onClose={() => setDeleteAllConfirm(false)}
        onConfirm={() => deleteAllMut.mutate()}
        message={`この会議の対話ログ ${visibleMessages.length}件 をすべて削除します。元に戻せません。この記録は参加者全員に共有されているものです。`}
      />

      <CreateInitiativeFromChatModal
        open={initiativeSource !== null}
        onClose={() => setInitiativeSource(null)}
        originMeetingId={meetingId}
        defaultTitle={initiativeSource?.title ?? ''}
        defaultDescription={initiativeSource?.description ?? ''}
        onCreated={() => setInitiativeSource(null)}
      />
    </section>
  );
}
